import {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  CSSProperties,
  FocusEvent,
  MouseEvent,
  ReactNode,
} from 'react';
import cn from 'classnames';
import { CloseCircleFilled } from '@ant-design/icons';
import IMask, { type InputMask } from 'imask';
import styles from './styles.module.css';

type PhoneMask = InputMask<{ mask: string }>;

const PHONE_MASK = '+{7} (000) 000-00-00';
const DEFAULT_PLACEHOLDER = '+7 (___) ___-__-__';
const PHONE_LENGTH = 10;

// IMask `prepare` — strips a leading country code from full international
// pastes so "+79991234567", "89991234567" and "9991234567" collapse to the
// same 10 digits.
const prepareNumber = (chars: string): string => {
  const digits = chars.replace(/\D/g, '');
  if (
    digits.length > PHONE_LENGTH &&
    (digits[0] === '7' || digits[0] === '8')
  ) {
    return digits.slice(1);
  }
  return chars;
};

// The mask's `{7}` is a fixed character, so IMask always keeps it in
// `unmaskedValue` ("79991234567"). These helpers translate between that
// representation and the caller-facing one (10 digits, no "7" prefix).
const toMaskValue = (value: string, stripCountryCode: boolean): string => {
  if (!value) return '';
  return stripCountryCode ? `7${value}` : value;
};

const fromMaskValue = (value: string, stripCountryCode: boolean): string => {
  if (!stripCountryCode) return value;
  return value.startsWith('7') ? value.slice(1) : value;
};

export interface PhoneInputProps {
  value?: string;
  onChange?: (digits: string) => void;
  placeholder?: string;
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
  allowClear?: boolean;
  prefix?: ReactNode;
  autoFocus?: boolean;
  id?: string;
  name?: string;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /**
   * Strip the leading "7" (country code) from both the emitted value and the
   * incoming `value` prop, so the caller deals in bare 10-digit strings.
   * Default: true.
   */
  stripCountryCode?: boolean;
}

export const PhoneInput: FC<PhoneInputProps> = memo(
  ({
    value = '',
    onChange,
    placeholder = DEFAULT_PLACEHOLDER,
    className,
    style,
    disabled,
    allowClear,
    prefix,
    autoFocus,
    id,
    name,
    onFocus,
    onBlur,
    stripCountryCode = true,
  }: PhoneInputProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const maskRef = useRef<PhoneMask | null>(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const stripCountryCodeRef = useRef(stripCountryCode);
    stripCountryCodeRef.current = stripCountryCode;

    const [focused, setFocused] = useState(false);

    // Initialise IMask once after the input has mounted.
    useEffect(() => {
      const el = inputRef.current;
      if (!el) return;

      const mask = IMask(el, {
        mask: PHONE_MASK,
        lazy: true,
        prepare: prepareNumber,
      }) as PhoneMask;
      mask.on('accept', () => {
        onChangeRef.current?.(
          fromMaskValue(mask.unmaskedValue, stripCountryCodeRef.current),
        );
      });
      maskRef.current = mask;

      return () => {
        mask.destroy();
        maskRef.current = null;
      };
    }, []);

    // Push external value changes (form reset, programmatic clear) into IMask.
    useEffect(() => {
      const mask = maskRef.current;
      if (!mask) return;
      const target = toMaskValue(value, stripCountryCode);
      if (mask.unmaskedValue !== target) {
        mask.unmaskedValue = target;
      }
    }, [value, stripCountryCode]);

    const handleFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        onFocus?.(event);
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setFocused(false);
        onBlur?.(event);
      },
      [onBlur],
    );

    const handleClear = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (maskRef.current) {
        maskRef.current.value = '';
      }
      inputRef.current?.focus();
    }, []);

    const needsWrapper = !!prefix || !!allowClear;
    const hasValue = value.length > 0;

    if (!needsWrapper) {
      return (
        <input
          ref={inputRef}
          className={cn(styles.input, className)}
          style={style}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          id={id}
          name={name}
          inputMode="tel"
          autoComplete="tel"
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      );
    }

    return (
      <span
        className={cn(
          styles.wrapper,
          {
            [styles.focused]: focused,
            [styles.disabled]: disabled,
          },
          className,
        )}
        style={style}
      >
        {prefix && <span className={styles.prefix}>{prefix}</span>}
        <input
          ref={inputRef}
          className={styles.bareInput}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          id={id}
          name={name}
          inputMode="tel"
          autoComplete="tel"
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {allowClear && (
          <span className={styles.suffix}>
            {hasValue && !disabled && (
              <button
                type="button"
                aria-label="clear"
                className={styles.clearIcon}
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClear}
              >
                <CloseCircleFilled />
              </button>
            )}
          </span>
        )}
      </span>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
